import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import {
  ArrowPathIcon,
  BanknotesIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EyeIcon,
  RectangleStackIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, Select, SortableTableHeader, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { usePermission } from '../../hooks/usePermissions'
import { useAuthContext } from '../../contexts/AuthContext'
import { useUserLogs } from '../../hooks/useUserLogs'
import { modulesService } from '../../services/modules'
import { estoqueService } from '../../services/estoque'
import { marketingNotasService } from '../../services/marketingNotas'
import type { EstoqueFornecedor, PaymentMethod } from '../../types/estoque'
import type {
  MarketingInvoiceStatus,
  MarketingPaymentDocumentFilter,
  MarketingPaymentFilters,
  MarketingPaymentListRow,
  MarketingPaymentStatus,
} from '../../types/marketingNotas'
import { paginateRows, sortRows } from '../Estoque/estoqueUi'
import EstoquePagination from '../Estoque/EstoquePagination'
import {
  formatCurrency,
  formatDateOnlyPtBr,
  statusBadgeClass,
  statusLabel,
} from './notasUi'
import {
  ICON_BTN_CLASS,
  MODULE_NAME,
  PAGE_SIZE,
  PERM_LOADING,
  buildPaymentGroups,
  isPaymentOverdue,
  paymentCompetenceLabel,
  paymentDocumentName,
  paymentDocumentKindBadgeClass,
  paymentDocumentKindLabel,
  paymentDocumentNumber,
  paymentDocumentStatus,
  paymentDocumentTitle,
  paymentStatusBadgeClass,
  paymentStatusLabel,
  paymentSupplierName,
  type FinanceiroViewMode,
} from './financeiroUi'
import FinanceiroDashboardTab from './FinanceiroDashboardTab'

type ActiveTab = 'listagem' | 'dashboard'

const tabClass = (activeTab: ActiveTab, tab: ActiveTab) =>
  `inline-flex items-center gap-1.5 py-3 px-1 border-b-2 font-medium text-sm ${
    activeTab === tab
      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
  }`

const DEFAULT_FILTERS: MarketingPaymentFilters = {
  invoiceStatus: 'confirmed',
  paymentStatus: '',
  documentType: '',
  supplierId: '',
  paymentMethodId: '',
  dueFrom: '',
  dueTo: '',
  requestNumber: '',
}

function sameFilters(a: MarketingPaymentFilters, b: MarketingPaymentFilters): boolean {
  return (
    (a.invoiceStatus || '') === (b.invoiceStatus || '') &&
    (a.paymentStatus || '') === (b.paymentStatus || '') &&
    (a.documentType || '') === (b.documentType || '') &&
    (a.supplierId || '') === (b.supplierId || '') &&
    (a.paymentMethodId || '') === (b.paymentMethodId || '') &&
    (a.dueFrom || '') === (b.dueFrom || '') &&
    (a.dueTo || '') === (b.dueTo || '') &&
    (a.requestNumber || '') === (b.requestNumber || '')
  )
}

const Financeiro: React.FC = () => {
  const { user, profile } = useAuthContext()
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { logUpdate, logView } = useUserLogs()

  const [moduleTitle, setModuleTitle] = useState('Financeiro')
  const [moduleDescription, setModuleDescription] = useState(
    'Relação financeira e baixa de parcelas de marketing'
  )
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [rows, setRows] = useState<MarketingPaymentListRow[]>([])
  const [fornecedores, setFornecedores] = useState<EstoqueFornecedor[]>([])
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])

  const [draftFilters, setDraftFilters] = useState<MarketingPaymentFilters>(DEFAULT_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState<MarketingPaymentFilters>(DEFAULT_FILTERS)

  const [sortKey, setSortKey] = useState('due_date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [activeTab, setActiveTab] = useState<ActiveTab>('listagem')
  const [viewMode, setViewMode] = useState<FinanceiroViewMode>('lista')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [viewRow, setViewRow] = useState<MarketingPaymentListRow | null>(null)

  useEffect(() => {
    modulesService.getByName(MODULE_NAME).then((result) => {
      if (result.success && result.data) {
        if (result.data.display_name) setModuleTitle(result.data.display_name)
        if (result.data.description) setModuleDescription(result.data.description)
      }
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [fornecedoresData, methodsData] = await Promise.all([
          estoqueService.listFornecedores(true),
          estoqueService.listFormasPagamento(true),
        ])
        if (cancelled) return
        setFornecedores(fornecedoresData)
        setPaymentMethods(methodsData.filter((m) => m.is_active))
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar os filtros.')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const loadPayments = useCallback(async (filters: MarketingPaymentFilters) => {
    setLoading(true)
    setError(null)
    try {
      const paymentsData = await marketingNotasService.listPayments(filters)
      setRows(paymentsData)
      setViewRow((current) => {
        if (!current) return null
        return paymentsData.find((row) => row.id === current.id) ?? null
      })
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
            ? err.message
            : ''
      setError(message.trim() || 'Erro ao carregar o financeiro.')
    } finally {
      setLoading(false)
    }
  }, [])

  const handleBuscar = () => {
    const next = { ...draftFilters }
    setAppliedFilters(next)
    setHasSearched(true)
    setPage(1)
    if (!sameFilters(next, appliedFilters)) setRows([])
    void loadPayments(next)
  }

  const handleLimparFiltros = () => {
    setDraftFilters(DEFAULT_FILTERS)
    setAppliedFilters(DEFAULT_FILTERS)
    setHasSearched(false)
    setRows([])
    setPage(1)
    setError(null)
    setViewRow(null)
  }

  const limparDisabled = loading || (sameFilters(draftFilters, DEFAULT_FILTERS) && !hasSearched)

  useEffect(() => {
    void logView('marketing_payment', null, 'Visualizou tela financeira de marketing')
  }, [logView])

  const sorted = useMemo(
    () =>
      sortRows(rows, sortKey, sortOrder, (row, key) => {
        if (key === 'due_date') return row.due_date
        if (key === 'amount') return Number(row.amount)
        if (key === 'installment') return Number(row.installment_number)
        if (key === 'method') return row.payment_method?.name || ''
        if (key === 'number') return paymentDocumentTitle(row)
        if (key === 'document_kind') return paymentDocumentKindLabel(row)
        if (key === 'supplier') return paymentSupplierName(row)
        if (key === 'invoice_status') return paymentDocumentStatus(row)
        if (key === 'payment_status') return row.payment_status
        if (key === 'paid_at') return row.paid_at || ''
        return ''
      }),
    [rows, sortKey, sortOrder]
  )

  const pageRows = paginateRows(sorted, page, PAGE_SIZE)
  const groups = useMemo(() => buildPaymentGroups(sorted), [sorted])
  const pageGroups = paginateRows(groups, page, PAGE_SIZE)

  const toggleGroup = (key: string) => {
    setExpandedGroups((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const changeViewMode = (mode: FinanceiroViewMode) => {
    setViewMode(mode)
    setPage(1)
    setExpandedGroups(new Set())
  }

  const totals = useMemo(() => {
    let pending = 0
    let paid = 0
    for (const row of rows) {
      const amount = Number(row.amount || 0)
      if (row.payment_status === 'paid') paid += amount
      else pending += amount
    }
    return {
      pending: Math.round(pending * 100) / 100,
      paid: Math.round(paid * 100) / 100,
      total: Math.round((pending + paid) * 100) / 100,
    }
  }, [rows])

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortOrder(key === 'due_date' || key === 'amount' ? 'asc' : 'asc')
    }
    setPage(1)
  }

  const paymentLogSnapshot = (row: MarketingPaymentListRow) => ({
    id: row.id,
    invoice_id: row.invoice_id,
    invoice_number: row.invoice?.number ?? null,
    amount: row.amount,
    due_date: row.due_date,
    payment_status: row.payment_status,
    paid_at: row.paid_at,
    paid_by: row.paid_by,
    paid_by_name: row.paid_by_user?.name ?? null,
    installment_number: row.installment_number,
    payment_method_id: row.payment_method_id,
    payment_method_name: row.payment_method?.name ?? null,
  })

  const handleSettle = async (row: MarketingPaymentListRow) => {
    if (paymentDocumentStatus(row) !== 'confirmed' || row.payment_status !== 'pending') return
    if (
      !window.confirm(
        `Baixar a parcela ${row.installment_number} de ${
          row.invoice ? `nota ${row.invoice.number}` : `contrato ${row.contract?.name || ''}`
        } no valor de ${formatCurrency(row.amount)}?`
      )
    ) {
      return
    }
    const actorId = profile?.id || user?.id
    if (!actorId) {
      setError('Não foi possível identificar o usuário para registrar a baixa.')
      return
    }
    setBusyId(row.id)
    setError(null)
    try {
      const updated = await marketingNotasService.settlePayment(row.id, actorId)
      void logUpdate(
        'marketing_payment',
        updated.id,
        `Baixou parcela ${updated.installment_number} da nota ${updated.invoice?.number || ''}`,
        paymentLogSnapshot(row),
        paymentLogSnapshot(updated)
      )
      await loadPayments(appliedFilters)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao baixar a parcela.')
    } finally {
      setBusyId(null)
    }
  }

  const handleReopen = async (row: MarketingPaymentListRow) => {
    if (row.payment_status !== 'paid') return
    if (
      !window.confirm(
        `Estornar a baixa da parcela ${row.installment_number} de ${
          row.invoice ? `nota ${row.invoice.number}` : `contrato ${row.contract?.name || ''}`
        }?`
      )
    ) {
      return
    }
    setBusyId(row.id)
    setError(null)
    try {
      const updated = await marketingNotasService.reopenPayment(row.id)
      void logUpdate(
        'marketing_payment',
        updated.id,
        `Estornou baixa da parcela ${updated.installment_number} da nota ${updated.invoice?.number || ''}`,
        paymentLogSnapshot(row),
        paymentLogSnapshot(updated)
      )
      await loadPayments(appliedFilters)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao estornar a parcela.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="flex h-full flex-1 flex-col overflow-x-hidden overflow-y-auto">
      <PermissionGuard
        moduleName={MODULE_NAME}
        permissionType="show"
        loadingComponent={PERM_LOADING}
        fallback={
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center">
              <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
                Acesso negado
              </Heading>
              <Text color="muted" className="mt-2">
                Você não tem permissão para visualizar o financeiro de marketing.
              </Text>
            </div>
          </div>
        }
      >
        <div className="flex h-full flex-col">
          <div className="border-b border-gray-200 p-6 dark:border-gray-700">
            <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
              {moduleTitle}
            </Heading>
            <Text size="sm" color="muted" className="mt-1">
              {moduleDescription}
            </Text>
          </div>

          <div className="px-6 py-4">
            <form
              className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
              onSubmit={(event) => {
                event.preventDefault()
                if (!loading) handleBuscar()
              }}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="min-w-0">
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Status
                  </label>
                  <Select
                    className="w-full"
                    value={draftFilters.invoiceStatus || ''}
                    onChange={(e) =>
                      setDraftFilters((f) => ({
                        ...f,
                        invoiceStatus: e.target.value as MarketingInvoiceStatus | '',
                      }))
                    }
                  >
                    <option value="">Todas</option>
                    <option value="confirmed">Confirmada</option>
                    <option value="draft">Rascunho</option>
                  </Select>
                </div>
                <div className="min-w-0">
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Status do pagamento
                  </label>
                  <Select
                    className="w-full"
                    value={draftFilters.paymentStatus || ''}
                    onChange={(e) =>
                      setDraftFilters((f) => ({
                        ...f,
                        paymentStatus: e.target.value as MarketingPaymentStatus | '',
                      }))
                    }
                  >
                    <option value="">Todos</option>
                    <option value="pending">Pendente</option>
                    <option value="paid">Pago</option>
                  </Select>
                </div>
                <div className="min-w-0">
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Tipo
                  </label>
                  <Select
                    className="w-full"
                    value={draftFilters.documentType || ''}
                    onChange={(e) =>
                      setDraftFilters((f) => ({
                        ...f,
                        documentType: e.target.value as MarketingPaymentDocumentFilter,
                      }))
                    }
                  >
                    <option value="">Todos</option>
                    <option value="nfe">NF-e</option>
                    <option value="nfse">NFS-e</option>
                    <option value="contract">Contrato</option>
                  </Select>
                </div>
                <div className="min-w-0">
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Fornecedor
                  </label>
                  <Select
                    className="w-full"
                    value={draftFilters.supplierId || ''}
                    onChange={(e) =>
                      setDraftFilters((f) => ({ ...f, supplierId: e.target.value }))
                    }
                  >
                    <option value="">Todos</option>
                    {fornecedores.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="min-w-0">
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Forma de pagamento
                  </label>
                  <Select
                    className="w-full"
                    value={draftFilters.paymentMethodId || ''}
                    onChange={(e) =>
                      setDraftFilters((f) => ({ ...f, paymentMethodId: e.target.value }))
                    }
                  >
                    <option value="">Todas</option>
                    {paymentMethods.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="min-w-0">
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Vencimento de
                  </label>
                  <Input
                    type="date"
                    className="w-full min-w-0"
                    value={draftFilters.dueFrom || ''}
                    onChange={(e) =>
                      setDraftFilters((f) => ({ ...f, dueFrom: e.target.value }))
                    }
                  />
                </div>
                <div className="min-w-0">
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Vencimento até
                  </label>
                  <Input
                    type="date"
                    className="w-full min-w-0"
                    value={draftFilters.dueTo || ''}
                    onChange={(e) =>
                      setDraftFilters((f) => ({ ...f, dueTo: e.target.value }))
                    }
                  />
                </div>
                <div className="min-w-0">
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Solicitação
                  </label>
                  <Input
                    className="w-full"
                    value={draftFilters.requestNumber || ''}
                    onChange={(e) =>
                      setDraftFilters((f) => ({ ...f, requestNumber: e.target.value }))
                    }
                    placeholder="#123"
                  />
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-3 dark:border-gray-700">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleLimparFiltros}
                  disabled={limparDisabled}
                >
                  Limpar filtros
                </Button>
                <Button type="submit" variant="solid" size="sm" disabled={loading}>
                  {loading ? 'Buscando...' : 'Buscar'}
                </Button>
              </div>
            </form>
          </div>

          {error && (
            <div className="mx-6 mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="px-6 pt-2">
            <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8">
                <button
                  type="button"
                  onClick={() => setActiveTab('listagem')}
                  className={tabClass(activeTab, 'listagem')}
                >
                  <TableCellsIcon className="h-4 w-4" />
                  Listagem
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('dashboard')}
                  className={tabClass(activeTab, 'dashboard')}
                >
                  <ChartBarIcon className="h-4 w-4" />
                  Dashboard
                </button>
              </nav>
            </div>
          </div>

          {activeTab === 'dashboard' && (
            <div className="px-6 pb-6">
              <FinanceiroDashboardTab
                rows={rows}
                loading={loading}
                hasSearched={hasSearched}
              />
            </div>
          )}

          {activeTab === 'listagem' && (
            <>
          {hasSearched && !(loading && rows.length === 0) && (
            <div className="mb-4 grid grid-cols-1 gap-3 px-6 sm:grid-cols-3">
              <div className="rounded-lg bg-white px-4 py-3 shadow dark:bg-gray-800">
                <Text size="xs" color="muted">
                  Pendente
                </Text>
                <Text className="font-semibold text-amber-700 dark:text-amber-300">
                  {formatCurrency(totals.pending)}
                </Text>
              </div>
              <div className="rounded-lg bg-white px-4 py-3 shadow dark:bg-gray-800">
                <Text size="xs" color="muted">
                  Pago
                </Text>
                <Text className="font-semibold text-green-700 dark:text-green-300">
                  {formatCurrency(totals.paid)}
                </Text>
              </div>
              <div className="rounded-lg bg-white px-4 py-3 shadow dark:bg-gray-800">
                <Text size="xs" color="muted">
                  Total filtrado
                </Text>
                <Text className="font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(totals.total)}
                </Text>
              </div>
            </div>
          )}

          <div className="px-6 pb-6">
            <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-6 py-4 dark:border-gray-700">
                <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                  {viewMode === 'lista' ? 'Parcelas' : 'Contratos e notas'}
                  {hasSearched && !(loading && rows.length === 0) && (
                    <span className="ml-2 text-base font-normal text-gray-500 dark:text-gray-400">
                      ({viewMode === 'lista' ? sorted.length : groups.length} registros)
                    </span>
                  )}
                </Heading>
                <nav className="flex gap-6">
                  <button
                    type="button"
                    className={`inline-flex items-center gap-1.5 border-b-2 py-2 text-sm font-medium ${
                      viewMode === 'lista'
                        ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                    onClick={() => changeViewMode('lista')}
                  >
                    <TableCellsIcon className="h-4 w-4" />
                    Lista
                  </button>
                  <button
                    type="button"
                    className={`inline-flex items-center gap-1.5 border-b-2 py-2 text-sm font-medium ${
                      viewMode === 'agrupado'
                        ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                    onClick={() => changeViewMode('agrupado')}
                  >
                    <RectangleStackIcon className="h-4 w-4" />
                    Por Nota/Contrato
                  </button>
                </nav>
              </div>
              {viewMode === 'lista' ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <SortableTableHeader
                        sortKey="due_date"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-2.5 py-3"
                      >
                        Vencimento
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="amount"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-2.5 py-3"
                      >
                        Valor
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="installment"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-2.5 py-3"
                      >
                        Parcela
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="method"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-2.5 py-3"
                      >
                        Forma
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="number"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-2.5 py-3"
                      >
                        Documento
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="document_kind"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-2.5 py-3"
                      >
                        Tipo
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="supplier"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-2.5 py-3"
                      >
                        Fornecedor
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="invoice_status"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-2.5 py-3"
                      >
                        Status nota
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="payment_status"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-2.5 py-3"
                      >
                        Pagamento
                      </SortableTableHeader>
                      <th className="px-2.5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                    {!hasSearched ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400"
                        >
                          Ajuste os filtros e clique em Buscar para carregar as parcelas.
                        </td>
                      </tr>
                    ) : loading && rows.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-4 py-10 text-center">
                          <LoadingSpinner />
                        </td>
                      </tr>
                    ) : pageRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400"
                        >
                          Nenhuma parcela encontrada com os filtros aplicados.
                        </td>
                      </tr>
                    ) : (
                      pageRows.map((row) => {
                        const overdue = isPaymentOverdue(row.payment_status, row.due_date)
                        const canSettle =
                          canUpdate &&
                          row.payment_status === 'pending' &&
                          paymentDocumentStatus(row) === 'confirmed'
                        const canReopen = canUpdate && row.payment_status === 'paid'
                        return (
                          <tr
                            key={row.id}
                            className={
                              overdue
                                ? 'bg-red-50 hover:bg-red-100/80 dark:bg-red-950/30 dark:hover:bg-red-950/45'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-900/30'
                            }
                          >
                            <td className="whitespace-nowrap px-2.5 py-3 text-sm text-gray-700 dark:text-gray-200">
                              <span className={overdue ? 'font-medium text-red-600 dark:text-red-300' : ''}>
                                {formatDateOnlyPtBr(row.due_date)}
                              </span>
                              {overdue && (
                                <span className="ml-2 text-xs text-red-500 dark:text-red-300">
                                  atrasada
                                </span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-2.5 py-3 text-sm font-medium text-gray-900 dark:text-white">
                              {formatCurrency(row.amount)}
                            </td>
                            <td className="whitespace-nowrap px-2.5 py-3 text-sm text-gray-700 dark:text-gray-200">
                              {row.installment_number}ª
                            </td>
                            <td className="px-2.5 py-3 text-sm text-gray-700 dark:text-gray-200">
                              {row.payment_method?.name || '—'}
                            </td>
                            <td className="px-2.5 py-3 text-sm text-gray-900 dark:text-white">
                              {paymentDocumentTitle(row)}
                            </td>
                            <td className="whitespace-nowrap px-2.5 py-3">
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${paymentDocumentKindBadgeClass(row)}`}
                              >
                                {paymentDocumentKindLabel(row)}
                              </span>
                            </td>
                            <td className="px-2.5 py-3 text-sm text-gray-700 dark:text-gray-200">
                              {paymentSupplierName(row) || '—'}
                            </td>
                            <td className="whitespace-nowrap px-2.5 py-3">
                              {paymentDocumentStatus(row) ? (
                                <span
                                  className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(paymentDocumentStatus(row))}`}
                                >
                                  {statusLabel(paymentDocumentStatus(row))}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="whitespace-nowrap px-2.5 py-3">
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${paymentStatusBadgeClass(row.payment_status)}`}
                              >
                                {paymentStatusLabel(row.payment_status)}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-2.5 py-3 text-right">
                              <div className="inline-flex items-center gap-1">
                                <button
                                  type="button"
                                  title="Detalhes"
                                  className={ICON_BTN_CLASS}
                                  onClick={() => {
                                    setViewRow(row)
                                    void logView(
                                      'marketing_payment',
                                      row.id,
                                      `Visualizou parcela ${row.installment_number} de ${row.invoice?.number || row.contract?.name || ''}`
                                    )
                                  }}
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </button>
                                {canSettle && (
                                  <PermissionGuard
                                    moduleName={MODULE_NAME}
                                    permissionType="update"
                                  >
                                    <button
                                      type="button"
                                      title="Baixar parcela"
                                      className={ICON_BTN_CLASS}
                                      disabled={busyId === row.id}
                                      onClick={() => void handleSettle(row)}
                                    >
                                      <BanknotesIcon className="h-4 w-4" />
                                    </button>
                                  </PermissionGuard>
                                )}
                                {canReopen && (
                                  <PermissionGuard
                                    moduleName={MODULE_NAME}
                                    permissionType="update"
                                  >
                                    <button
                                      type="button"
                                      title="Estornar baixa"
                                      className={ICON_BTN_CLASS}
                                      disabled={busyId === row.id}
                                      onClick={() => void handleReopen(row)}
                                    >
                                      <ArrowPathIcon className="h-4 w-4" />
                                    </button>
                                  </PermissionGuard>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
              ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-2.5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Documento
                      </th>
                      <th className="px-2.5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Tipo
                      </th>
                      <th className="px-2.5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Fornecedor
                      </th>
                      <th className="px-2.5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Parcelas
                      </th>
                      <th className="px-2.5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Valor
                      </th>
                      <th className="px-2.5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Pendente
                      </th>
                      <th className="px-2.5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Próximo vencimento
                      </th>
                      <th className="px-2.5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                    {!hasSearched ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                          Ajuste os filtros e clique em Buscar para carregar os registros.
                        </td>
                      </tr>
                    ) : loading && rows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-10 text-center">
                          <LoadingSpinner />
                        </td>
                      </tr>
                    ) : pageGroups.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                          Nenhum contrato ou nota encontrado com os filtros aplicados.
                        </td>
                      </tr>
                    ) : (
                      pageGroups.map((group) => {
                        const open = expandedGroups.has(group.key)
                        const sample = group.rows[0]
                        return (
                          <Fragment key={group.key}>
                            <tr
                              className={`cursor-pointer ${
                                group.overdue
                                  ? 'bg-red-50 hover:bg-red-100/80 dark:bg-red-950/30 dark:hover:bg-red-950/45'
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-900/30'
                              }`}
                              onClick={() => toggleGroup(group.key)}
                            >
                              <td className="px-2.5 py-3 text-sm text-gray-900 dark:text-white">
                                <div className="flex items-center gap-2">
                                  {open ? (
                                    <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
                                  ) : (
                                    <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" />
                                  )}
                                  <span className="font-medium">{group.title}</span>
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-2.5 py-3">
                                {sample ? (
                                  <span
                                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${paymentDocumentKindBadgeClass(sample)}`}
                                  >
                                    {paymentDocumentKindLabel(sample)}
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td className="px-2.5 py-3 text-sm text-gray-700 dark:text-gray-200">
                                {group.supplierName}
                              </td>
                              <td className="whitespace-nowrap px-2.5 py-3 text-sm text-gray-700 dark:text-gray-200">
                                {group.paidCount}/{group.rows.length} pagas
                              </td>
                              <td className="whitespace-nowrap px-2.5 py-3 text-sm font-medium text-gray-900 dark:text-white">
                                {formatCurrency(group.total)}
                              </td>
                              <td className="whitespace-nowrap px-2.5 py-3 text-sm text-gray-700 dark:text-gray-200">
                                {formatCurrency(group.pending)}
                              </td>
                              <td className="whitespace-nowrap px-2.5 py-3 text-sm text-gray-700 dark:text-gray-200">
                                <span className={group.overdue ? 'font-medium text-red-600 dark:text-red-300' : ''}>
                                  {formatDateOnlyPtBr(group.nextDue)}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-2.5 py-3">
                                {group.status ? (
                                  <span
                                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(group.status)}`}
                                  >
                                    {statusLabel(group.status)}
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </td>
                            </tr>
                            {open && (
                              <tr key={`${group.key}-detail`} className="bg-gray-50 dark:bg-gray-900/40">
                                <td colSpan={8} className="px-4 py-3">
                                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead>
                                      <tr>
                                        {['Vencimento', 'Valor', 'Parcela', 'Forma', 'Pagamento', 'Pago em', 'Baixado por', 'Ações'].map((label) => (
                                          <th
                                            key={label}
                                            className={`px-2.5 py-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 ${
                                              label === 'Ações' ? 'text-right' : 'text-left'
                                            }`}
                                          >
                                            {label}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                      {group.rows.map((row) => {
                                        const overdue = isPaymentOverdue(row.payment_status, row.due_date)
                                        const canSettle =
                                          canUpdate &&
                                          row.payment_status === 'pending' &&
                                          paymentDocumentStatus(row) === 'confirmed'
                                        const canReopen = canUpdate && row.payment_status === 'paid'
                                        return (
                                          <tr
                                            key={row.id}
                                            className={
                                              overdue
                                                ? 'bg-red-50 dark:bg-red-950/30'
                                                : 'bg-white dark:bg-gray-800'
                                            }
                                          >
                                            <td className="whitespace-nowrap px-2.5 py-2 text-sm text-gray-700 dark:text-gray-200">
                                              <span className={overdue ? 'font-medium text-red-600 dark:text-red-300' : ''}>
                                                {formatDateOnlyPtBr(row.due_date)}
                                              </span>
                                              {overdue && (
                                                <span className="ml-2 text-xs text-red-500 dark:text-red-300">
                                                  atrasada
                                                </span>
                                              )}
                                            </td>
                                            <td className="whitespace-nowrap px-2.5 py-2 text-sm font-medium text-gray-900 dark:text-white">
                                              {formatCurrency(row.amount)}
                                            </td>
                                            <td className="whitespace-nowrap px-2.5 py-2 text-sm text-gray-700 dark:text-gray-200">
                                              {row.installment_number}ª
                                            </td>
                                            <td className="px-2.5 py-2 text-sm text-gray-700 dark:text-gray-200">
                                              {row.payment_method?.name || '—'}
                                            </td>
                                            <td className="whitespace-nowrap px-2.5 py-2">
                                              <span
                                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${paymentStatusBadgeClass(row.payment_status)}`}
                                              >
                                                {paymentStatusLabel(row.payment_status)}
                                              </span>
                                            </td>
                                            <td className="whitespace-nowrap px-2.5 py-2 text-sm text-gray-700 dark:text-gray-200">
                                              {row.paid_at ? formatDateOnlyPtBr(row.paid_at.slice(0, 10)) : '—'}
                                            </td>
                                            <td className="px-2.5 py-2 text-sm text-gray-700 dark:text-gray-200">
                                              {row.paid_by_user?.name || '—'}
                                            </td>
                                            <td className="whitespace-nowrap px-2.5 py-2 text-right">
                                              <div className="inline-flex items-center gap-1">
                                                <button
                                                  type="button"
                                                  title="Detalhes"
                                                  className={ICON_BTN_CLASS}
                                                  onClick={() => {
                                                    setViewRow(row)
                                                    void logView(
                                                      'marketing_payment',
                                                      row.id,
                                                      `Visualizou parcela ${row.installment_number} de ${row.invoice?.number || row.contract?.name || ''}`
                                                    )
                                                  }}
                                                >
                                                  <EyeIcon className="h-4 w-4" />
                                                </button>
                                                {canSettle && (
                                                  <PermissionGuard moduleName={MODULE_NAME} permissionType="update">
                                                    <button
                                                      type="button"
                                                      title="Baixar parcela"
                                                      className={ICON_BTN_CLASS}
                                                      disabled={busyId === row.id}
                                                      onClick={() => void handleSettle(row)}
                                                    >
                                                      <BanknotesIcon className="h-4 w-4" />
                                                    </button>
                                                  </PermissionGuard>
                                                )}
                                                {canReopen && (
                                                  <PermissionGuard moduleName={MODULE_NAME} permissionType="update">
                                                    <button
                                                      type="button"
                                                      title="Estornar baixa"
                                                      className={ICON_BTN_CLASS}
                                                      disabled={busyId === row.id}
                                                      onClick={() => void handleReopen(row)}
                                                    >
                                                      <ArrowPathIcon className="h-4 w-4" />
                                                    </button>
                                                  </PermissionGuard>
                                                )}
                                              </div>
                                            </td>
                                          </tr>
                                        )
                                      })}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
              )}
              <div className="px-4 pb-4">
                <EstoquePagination
                  page={page}
                  pageSize={PAGE_SIZE}
                  total={viewMode === 'lista' ? sorted.length : groups.length}
                  onPageChange={setPage}
                />
              </div>
            </div>
          </div>
            </>
          )}
        </div>

        {viewRow && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={() => setViewRow(null)} />
            <div className="relative z-10 w-full max-w-lg rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800">
              <div className="mb-4 flex items-center justify-between">
                <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                  Parcela {viewRow.installment_number}ª
                </Heading>
                <Button type="button" variant="outline" size="sm" onClick={() => setViewRow(null)}>
                  Fechar
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Text size="xs" color="muted">
                    Vencimento
                  </Text>
                  <Text>{formatDateOnlyPtBr(viewRow.due_date)}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Valor
                  </Text>
                  <Text>{formatCurrency(viewRow.amount)}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Forma
                  </Text>
                  <Text>{viewRow.payment_method?.name || '—'}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Status pagamento
                  </Text>
                  <Text>{paymentStatusLabel(viewRow.payment_status)}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    {viewRow.contract ? 'Nome' : 'Número'}
                  </Text>
                  <Text>
                    {viewRow.contract ? paymentDocumentName(viewRow) : paymentDocumentNumber(viewRow)}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Tipo
                  </Text>
                  <Text>{paymentDocumentKindLabel(viewRow)}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Fornecedor
                  </Text>
                  <Text>{paymentSupplierName(viewRow) || '—'}</Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Status do documento
                  </Text>
                  <Text>
                    {paymentDocumentStatus(viewRow) ? statusLabel(paymentDocumentStatus(viewRow)) : '—'}
                  </Text>
                </div>
                {viewRow.contract && viewRow.recurrence_competence ? (
                  <div>
                    <Text size="xs" color="muted">
                      Competência
                    </Text>
                    <Text>{paymentCompetenceLabel(viewRow)}</Text>
                  </div>
                ) : null}
                <div>
                  <Text size="xs" color="muted">
                    Pago em
                  </Text>
                  <Text>
                    {viewRow.paid_at
                      ? formatDateOnlyPtBr(viewRow.paid_at.slice(0, 10))
                      : '—'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Baixado por
                  </Text>
                  <Text>{viewRow.paid_by_user?.name || '—'}</Text>
                </div>
              </div>
            </div>
          </div>
        )}
      </PermissionGuard>
    </div>
  )
}

export default Financeiro
